'use client';

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Calendar } from 'libs/features/tasks-calendar/src/lib/calendar';
import { TasksController } from '@nx-frontend/features/tasks-calendar';
import { TasksManager } from '@nx-frontend/features/web-editor/plugins';
import { TasksSocketService, TaskEventsService } from '@nx-frontend/features/web-editor/plugins';
import { CommentService } from '@nx-frontend/features/web-editor/plugins/commentManager';
import { loadJs, loadCss } from '@nx-frontend/features/web-editor/util';
import { Head } from '@nx-frontend/shared/ui';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { generalEnv } from '@nx-frontend/shared/env';
import { useRouter, useParams, useSearchParams } from '@nx-frontend/shared/hooks/router-proxy';
import { SocketContext } from '@nx-frontend/shared/providers';
import { $app, $language } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { setWorkspaceByIdEvent } from '@nx-frontend/shared/store/current-workspace';
import { fetchNoteEvent } from '@nx-frontend/shared/store/notes';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $user } from '@nx-frontend/shared/store/user';
import { $wpCanManageMembers, $workspaceAccess } from '@nx-frontend/shared/store/workspace-access';
import { $workspacesIds } from '@nx-frontend/shared/store/workspaces';
import { TaskSubjectDashboardFilterChanges, TasksListType } from '@nx-frontend/shared/types';
import { WorkspacePrivilege } from '@nx-frontend/shared/types';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { sendOpenTaskEvent } from '@nx-frontend/shared/utils';
import { NextRouterType } from '@nx-frontend/shared/utils';
import { webnotesClientEnv } from '@nx-frontend/webnotes-client/shared';
import { getOpenInvitePopup } from '@nx-frontend/widgets/popups';

const customElementSelector = 'nim-task-dashboard';

let isDashboardInit = false;

export const isDashboardBundleLoaded = () => {
  if (typeof document === 'object') {
    return document.createElement(customElementSelector).constructor !== HTMLElement;
  }
  return false;
};

export function Tasks() {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const slug = useMemo(() => params?.slug || [], [params?.slug]) as string[];
  const socket = useContext(SocketContext);
  const componentRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(isDashboardBundleLoaded());
  const [
    currentWorkspace,
    currentOrganization,
    user,
    app,
    role,
    orgInfo,
    billingInfo,
    canManageMembers,
    workspaceAccess,
    unleashFlags,
    workspacesIds,
    language,
  ] = useUnit([
    $currentWorkspace,
    $currentOrganization,
    $user,
    $app,
    $orgRoleState,
    $orgInfo,
    $billingInfo,
    $wpCanManageMembers,
    $workspaceAccess,
    $unleashFlags,
    $workspacesIds,
    $language,
  ]);
  const [openModal, setWorkspaceById, fetchNote] = useUnit([openModalEvent, setWorkspaceByIdEvent, fetchNoteEvent]);

  const { toast } = useToast();
  const t = useTranslations();
  const tFeatures = useTranslations('features');

  const routerType = NextRouterType.APP;
  const routerPushFn = router.push;
  const [showCalendar, setShowCalendar] = useState(false);
  const controller = TasksController.getInstance();

  const onChangeCalendarView = useCallback(
    ({ featureId, isCalendarEnabled }: { featureId: string; isCalendarEnabled: boolean }) => {
      if (featureId === 'dashboard') {
        setShowCalendar(isCalendarEnabled);
      }
    },
    []
  );

  const onFilterChanged = useCallback(
    (filters: TaskSubjectDashboardFilterChanges) => {
      if (filters.uniqueId === currentWorkspace.globalId) {
        const { tab, labels, assigneeIds, date, workspaceIds } = filters;
        controller.emitter.emit('dashboard-tab-changed', {
          featureId: 'tasks-dashboard-' + currentWorkspace.globalId,
          tab,
        });
        const newFilters = { labels, assigneeIds, date, workspaceIds };
        controller.emitter.emit('filters-changed', {
          featureId: 'tasks-dashboard-' + currentWorkspace.globalId,
          filters: newFilters,
        });
      }
    },
    [currentWorkspace.globalId, controller]
  );

  const completeTask = useCallback(
    (taskId: string, done: boolean, taskWorkspaceId?: string) => {
      const canEdit = workspaceAccess.privileges?.includes(WorkspacePrivilege.CAN_EDIT) ?? false;
      if (!canEdit) return;
      controller.completeTask(
        taskId,
        done,
        taskWorkspaceId !== undefined ? taskWorkspaceId : currentWorkspace.globalId
      );
    },
    [controller, workspaceAccess.privileges, currentWorkspace.globalId]
  );

  useEffect(() => {
    if (unleashFlags[UnleashFeatures.DASHBOARD_CALENDAR]) {
      // init tasks calendar controller
      const canEdit = workspaceAccess.privileges?.includes(WorkspacePrivilege.CAN_EDIT) ?? false;
      const canComment = workspaceAccess.privileges?.includes(WorkspacePrivilege.CAN_COMMENT) ?? false;
      controller.init(app.sessionId, generalEnv.gatewayUrl, socket, tFeatures, {
        staticAssetPrefix: webnotesClientEnv.staticAssetPrefix,
        nimAllowedActions: {
          ...TasksManager.AllTaskActionsAllowed(canEdit),
          taskCommentAdd: canComment,
        },
        workspacesIds,
      });
      controller.emitter.on('change-calendar-view', onChangeCalendarView);
      controller.emitter.on('dashboard-filters-changed', onFilterChanged);
      return () => {
        controller.emitter.off('change-calendar-view', onChangeCalendarView);
        controller.emitter.off('dashboard-filters-changed', onFilterChanged);
      };
    }
  }, [
    app.sessionId,
    onChangeCalendarView,
    controller,
    unleashFlags,
    socket,
    onFilterChanged,
    workspaceAccess.privileges,
    workspacesIds,
    tFeatures,
  ]);

  /**
   * Init tasks and comments socket
   */
  useEffect(() => {
    if (socket) {
      TasksSocketService.init(socket);
      CommentService.init(socket, {
        sessionId: app.sessionId,
        workspaceId: currentWorkspace.globalId,
      });
    }
  }, [socket, app.sessionId, currentWorkspace.globalId]);

  /**
   * Init tasks events service
   */
  useEffect(() => {
    TaskEventsService.goToPage = (workspaceId: string, noteId: string): void => {
      setWorkspaceById(workspaceId);
      fetchNote({ workspaceId, globalId: noteId });
      router.push(`/space/${workspaceId}/page/${noteId}`);
    };
    TaskEventsService.openInvitePopup = getOpenInvitePopup(
      { role, orgInfo, billingInfo, currentWorkspace, canManageMembers },
      openModal,
      { routerType, routerPushFn }
    );
    TaskEventsService.init(toast, t);
  }, [
    router,
    role,
    orgInfo,
    billingInfo,
    currentWorkspace,
    canManageMembers,
    toast,
    t,
    openModal,
    setWorkspaceById,
    fetchNote,
    routerType,
    routerPushFn,
  ]);

  /**
   * Callback for when dashboard is loaded
   */
  const onDashboardLoaded = useCallback(() => {
    const setupApiEvent = new CustomEvent('ng2:tasks:setup-api', {
      detail: {
        gatewayUrl: generalEnv.gatewayUrl,
        gatewayToken: app.sessionId,
        staticAssetPrefix: webnotesClientEnv.staticAssetPrefix,
        userId: user.id,
        userRole: role.role,
        workspaceId: currentWorkspace.globalId,
        orgId: currentOrganization.globalId,
      },
    });

    document.dispatchEvent(setupApiEvent);

    if (!isDashboardInit) {
      isDashboardInit = true;

      const setupAttachmentApiEvent = new CustomEvent('ng2:attachments:setup-api', {
        detail: {
          gatewayUrl: generalEnv.gatewayUrl,
          gatewayToken: app.sessionId,
          staticAssetPrefix: webnotesClientEnv.staticAssetPrefix,
        },
      });

      document.dispatchEvent(setupAttachmentApiEvent);

      const loadAllEvent = new CustomEvent('ng2:tasks:load-all', {
        detail: {
          workspaceId: currentWorkspace.globalId,
        },
      });

      document.dispatchEvent(loadAllEvent);
    }

    setIsLoaded(true);

    let [, , nimTaskId] = slug;
    if (!nimTaskId && typeof searchParams?.get('featParams') === 'string') {
      nimTaskId = searchParams?.get('featParams') as string;
    }

    if (document?.querySelectorAll(customElementSelector).length === 0) {
      const nimTaskDashboard = document.createElement(customElementSelector);
      nimTaskDashboard.style.flex = 'none';
      const canEdit = workspaceAccess.privileges?.includes(WorkspacePrivilege.CAN_EDIT) ?? false;
      const canComment = workspaceAccess.privileges?.includes(WorkspacePrivilege.CAN_COMMENT) ?? false;
      Object.assign(nimTaskDashboard, {
        nimFullWidth: true,
        nimAllowedActions: {
          ...TasksManager.AllTaskActionsAllowed(canEdit),
          taskCommentAdd: canComment,
        },
        nimLanguage: language,
        nimDetailedViewFloating: false,
        nimTaskId,
        nimShowBreadcrumbs: true,
        nimShowCalendarSwitch: unleashFlags[UnleashFeatures.DASHBOARD_CALENDAR],
        nimEmptyErrorTitle: 'Your task dashboard is empty',
        nimEmptyErrorText: 'This area serves as a hub for aggregating tasks from both the internal space and portals.',
      });
      componentRef.current?.insertBefore(nimTaskDashboard, componentRef.current?.firstChild);
    }

    setTimeout(() => {
      sendOpenTaskEvent(nimTaskId, true);
    }, 10);
  }, [
    app.sessionId,
    user.id,
    role.role,
    currentWorkspace.globalId,
    currentOrganization.globalId,
    slug,
    searchParams,
    workspaceAccess.privileges,
    unleashFlags,
    language,
  ]);

  /**
   * Load tasks bundle js and css
   */
  useEffect(() => {
    loadJs({
      id: 'tasks-bundle-script',
      url: generalEnv.tasksBundleJsUrl,
    });

    loadCss({
      id: 'tasks-bundle-css',
      url: generalEnv.tasksBundleCssUrl,
    });

    let IID: NodeJS.Timer | number | null = null;
    if (!isDashboardBundleLoaded()) {
      IID = setInterval(() => {
        if (isDashboardBundleLoaded()) {
          onDashboardLoaded();
          IID && clearInterval(IID);
        }
      }, 500);
    } else {
      onDashboardLoaded();
    }

    return () => {
      IID && clearInterval(IID);
      IID && document?.querySelector(customElementSelector)?.remove();
    };
  }, [onDashboardLoaded]);

  const pageTitle = `Tasks - ${currentWorkspace.title}`;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <div
        className={classNames('workspace-tasks-dashboard py-[12px] px-[29px] pr-0 w-full h-full', {
          'calendar-open flex flex-col overflow-hidden': showCalendar,
        })}
        ref={componentRef}
      >
        {!isLoaded && (
          <div className="h-full w-full flex justify-center items-center">
            <Spinner />
          </div>
        )}
        {unleashFlags[UnleashFeatures.DASHBOARD_CALENDAR] && (
          <div className={classNames('w-full pl-8 pr-10 h-full overflow-hidden', { hidden: !showCalendar })}>
            <div className="w-full h-full flex flex-col" data-gramm={false}>
              <Calendar
                featureId={'tasks-dashboard-' + currentWorkspace.globalId}
                workspaceId={currentWorkspace.globalId}
                tasks={[]}
                completeTask={completeTask}
                t={tFeatures}
                options={{
                  allowCreateNewTask: false,
                }}
                controller={controller}
                type={TasksListType.DASHBOARD}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
